import {
  Box,
  Button,
  DashboardModule,
  Form,
  Image,
  SaveFormModule,
  SidebarNoticeBox,
} from '@domain-group/suit-theme'

import useAttachments from '@hooks/useAttachments'
import useCarousel from '@hooks/useCarousel'

import handleCallbackAction from '@functions/handleCallbackAction'

import CarouselControls from '@components/CarouselControls'
import Watermark from '@components/Watermark'

import type { UseBrandPayload } from '@hooks/useBrand'
import type { UseFormPayload } from '@domain-group/react-hooks'

import WatermarkSettingToggles from './WatermarkSettingToggles'

import watermarkBaseImage1 from '../../assets/watermarkBase1.jpg'
import watermarkBaseImage2 from '../../assets/watermarkBase2.jpg'
import watermarkBaseImage3 from '../../assets/watermarkBase3.jpg'
import watermarkBaseImage4 from '../../assets/watermarkBase4.jpg'

const imageUrls = [watermarkBaseImage1, watermarkBaseImage2, watermarkBaseImage3, watermarkBaseImage4]

interface WatermarkSettingsProps {
  brandId: number,
  callbacks: {
    updateBrand: UseBrandPayload['callbacks']['updateBrand'],
  },
  formPayload: UseFormPayload,
  updating: boolean,
}

const WatermarkSettings = (props: WatermarkSettingsProps) => {
  const {
    brandId, callbacks: { updateBrand }, formPayload, updating,
  } = props

  const {
    entityState,
    entityState: {
      options: {
        export_settings,
        watermark_images,
        watermark_settings,
      },
    },
    handlers,
    setEntityState,
  } = formPayload

  const opacity = watermark_settings?.opacity || 0.7
  const position = watermark_settings?.position || 'south-east'
  const horizontal = watermark_settings?.padding?.horizontal || 20
  const vertical = watermark_settings?.padding?.vertical || 20

  const { filteredAttachments } = useAttachments({
    filters: {
      attachable_id: brandId,
      attachable_type: 'Brand',
      attachment_type: 'watermark_image',
    },
  })
  const watermarkImage = filteredAttachments[0] || {}

  const watermarkImageUrl = watermarkImage?.links?.original_url || ''

  const exportSize = export_settings?.size || 'original'
  const height = export_settings?.height
  const width = export_settings?.width

  const updateBrandPayload = {
    callbacks: {
      action: updateBrand,
    },
    entityParams: entityState,
    toastText: 'Watermark Settings updated',
  }

  const carouselPayload = useCarousel({ initialIndex: 0, items: imageUrls })
  const { selectedIndex } = carouselPayload

  const isWatermarkOnExport = watermark_images?.includes('export')
  const isWatermarkOnSoldOrLeased = watermark_images?.includes('sold_or_leased')

  return (
    <>
      <DashboardModule title="Image Export Settings">
        <Form padding="large">
          <Box justifyContent="center">
            <Box position="relative" maxWidth={600} maxHeight={[300, 400]}>
              <Image
                borderRadius="5px"
                height={[250, 400]}
                url={imageUrls[selectedIndex!]}
              />

              <CarouselControls carouselPayload={carouselPayload} />

              <Watermark
                watermarkImageUrl={watermarkImageUrl}
                watermarkSettings={{
                  height: watermarkImage?.dimensions?.original?.height || 200,
                  horizontal,
                  opacity,
                  position,
                  vertical,
                  width: watermarkImage?.dimensions?.original?.width || 200,
                }}
              />
            </Box>
          </Box>

          <Form.Row marginTop="large">
            <Form.Field label="Export Size">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                name="options.export_settings.size"
                value={exportSize}
                {...handlers}
              >
                <option value="original">Original</option>
                <option value="custom">Custom Size</option>
              </select>
            </Form.Field>

            {exportSize === 'custom' && (
              // @ts-expect-error TS2322
              <Form.Row marginTop={['large', 0]}>
                <Form.Field label="Width">
                  <input
                    name="options.export_settings.width"
                    type="number"
                    value={width}
                    {...handlers}
                  />
                </Form.Field>

                {/*
                 // @ts-expect-error TS2322 */}
                <Form.Field label="Height" marginTop={['large', 0]}>
                  <input
                    name="options.export_settings.height"
                    type="number"
                    value={height}
                    {...handlers}
                  />
                </Form.Field>
              </Form.Row>
            )}
          </Form.Row>

          <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarNoticeBox.Title>Watermark Recommendation</SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              Realestate.com.au and Domain.com.au watermark specifications recommend pinning your watermark to the
              bottom right. Padding and size of watermark will vary based on the final image output dimensions.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>

          <Form.Row>
            <Form.Field label="Pin to Corner">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                name="options.watermark_settings.position"
                value={position}
                {...handlers}
              >
                <option value="south-east">Bottom Right</option>
                <option value="south-west">Bottom Left</option>
                <option value="north-east">Top Right</option>
                <option value="north-west">Top Left</option>
              </select>
            </Form.Field>

            {/*
             // @ts-expect-error TS2322 */}
            <Form.Row marginTop={['large', 0]}>
              <Form.Field label="Padding (H)">
                <input
                  name="options.watermark_settings.padding.horizontal"
                  type="number"
                  value={horizontal}
                  {...handlers}
                />
              </Form.Field>

              {/*
               // @ts-expect-error TS2322 */}
              <Form.Field label="Padding (V)" marginTop={['large', 0]}>
                <input
                  name="options.watermark_settings.padding.vertical"
                  type="number"
                  value={vertical}
                  {...handlers}
                />
              </Form.Field>
            </Form.Row>
          </Form.Row>

          {/*
           // @ts-expect-error TS2322 */}
          <Form.Row padding="large">
            <Form.Field label="Transparency" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                name="options.watermark_settings.opacity"
                value={opacity}
                {...handlers}
              >
                <option value="0.1">10%</option>
                <option value="0.2">20%</option>
                <option value="0.3">30%</option>
                <option value="0.4">40%</option>
                <option value="0.5">50%</option>
                <option value="0.6">60%</option>
                <option value="0.7">70%</option>
                <option value="0.8">80%</option>
                <option value="0.9">90%</option>
                <option value="1.0">100%</option>
              </select>
            </Form.Field>
          </Form.Row>

          <WatermarkSettingToggles
            entityState={entityState}
            setEntityState={setEntityState}
            isWatermarkOnExport={isWatermarkOnExport}
            isWatermarkOnSoldOrLeased={isWatermarkOnSoldOrLeased}
            watermark_images={watermark_images}
          />
        </Form>
      </DashboardModule>

      <SaveFormModule>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          // @ts-expect-error TS2322
          marginLeft="auto"
          onClick={() => handleCallbackAction(updateBrandPayload)}
          size="medium"
        >
          Save Details
        </Button>
      </SaveFormModule>
    </>
  )
}

export default WatermarkSettings
